import { Injectable, Injector } from '@angular/core';
import { EntityModel } from '../models/entity-model';
import { DefaultElements } from '../config/elements';
import { trans } from '../../localization/helpers';
import { ApiInterface, ApiService } from '../../api/api.service';
import { SelectService } from '../../forms/form/services/select.service';
import { clone } from '../../support/helpers';

@Injectable()
export class EntityService extends ApiService implements ApiInterface {

    public restEndpoint = 'entities';
    public model        = EntityModel;
    public collection;

    protected cache = true;

    protected localCache = true;

    protected cacheExpire = 60 * 10 * 1000;

    protected availableElements = [];

    public constructor(
        public injector: Injector,
        protected selectService: SelectService,
    ) {
        super(injector);

        // set default elements
        this.availableElements = this.availableElements.concat(DefaultElements);
    }

    /**
     * Returns whether the current user has permissions for the entity
     *
     * @param entityName
     * @returns {Array<any>}
     */
    public hasPermissionsForEntity(entityName) {

        const entity = this.find('name', entityName);

        return entity.roles_permissions;
    }

    /**
     * Get the default form for the given entity
     *
     * @param entityName
     */
    public getDefaultForm(entityName) {

        const entity = this.find('name', entityName);

        return entity.forms().first();
    }

    /**
     * Get the default form for the given entity
     *
     * @param entityName
     */
    public getFormById(entityName, formId) {

        const entity = this.find('name', entityName);

        return entity.forms().find(form => form.id === formId);
    }

    /**
     * Returns the columns that should show up in the datatable
     *
     * @param entityName
     * @returns {[{name, data, title, searchable: boolean},{name, data, title, searchable: boolean},{name, data, title, searchable: boolean},{name, data, title, searchable: boolean},{name, data, title, searchable: boolean}]}
     */
    public getDatatableColumns(entityName) {

        const entity = this.find('name', entityName);

        return entity
            .fieldsMeta().all()
            .filter(fieldMeta => {
                // exclude fields that should not be included in the datatable at all
                return fieldMeta.config.available_in_overview;
            })
            .map(fieldMeta => {
                let filter;

                switch (fieldMeta.type) {
                    case 'currency':
                    case 'number':
                        filter = {
                            'filter_type': 'range_number',
                            'filter_delay': 1000,
                        };
                        break;

                    case 'date':
                    case 'time':
                        filter = {
                            'filter_type': 'range_date',
                            'datepicker_type': 'bootstrap-datetimepicker',
                            'date_format': 'YYYY-MM-DD'
                        };
                        break;

                    // Not viable since it only shows the options that are displayed on the current (filtered) "page"
                    case 'select':
                        filter = {
                            'filter_type': 'select',
                            'select_type': 'select2',
                            'select_type_options': {
                                allowClear: true,
                                placeholder: trans('Select value')
                            }
                        };

                        if (fieldMeta.config && fieldMeta.config.relation) {
                            // console.log('field meta', fieldMeta);
                            filter['select_type_options']['ajax'] = {
                                url: fieldMeta.config.url,
                                headers: {
                                    Authorization: 'Bearer ' + this.authService.getToken()
                                },
                                processResults: (data) => {
                                    return {
                                        results: data.data.map(row => {
                                            return {
                                                id: row[fieldMeta.config.searchDisplayColumn],
                                                text: row[fieldMeta.config.searchDisplayColumn]
                                            }
                                        })
                                    }
                                },
                                data: (params: { [key: string]: any }) => {
                                    params = clone(params);

                                    delete params._type;

                                    const columns = fieldMeta.config.searchQueryColumn.split(';');
                                    const query   = params.term;

                                    params.search = this.selectService.search(
                                        query,
                                        columns,
                                    );

                                    return params;
                                }
                            }
                        } else if (fieldMeta.config && fieldMeta.config.options) {
                            filter['data'] = fieldMeta.config.options.map(option => {
                                return {value: option.key, label: option.value};
                            });
                        }

                        break;

                    case 'checkbox':
                    case 'email':
                    case 'media':
                    case 'options':
                    case 'password':
                    case 'radio':
                    case 'select':
                    case 'tree':
                    case 'text':
                    case 'textarea':
                    case 'url':
                    default:
                        filter = {
                            'filter_type': 'text'
                        };
                        break;
                }

                const column = {
                    name: fieldMeta.name,
                    data: fieldMeta.name,
                    title: trans(fieldMeta.label.translate()), // @TODO: Label translation should'nt
                    // need to be translated again. Needs fix.
                    visible: fieldMeta.config.show_in_overview,
                    searchable: true
                };

                if (fieldMeta.config.filter === undefined || fieldMeta.config.filter) {
                    column['filter'] = filter;
                }

                if (['currency', 'number', 'date', 'time'].indexOf(fieldMeta.type) >= 0) {
                    column['width'] = '200px';
                }

                if (['select'].indexOf(fieldMeta.type) >= 0) {
                    column['width'] = '100px';
                }

                return column;
            });
    }

    /**
     * Returns the entity when found
     *
     * @param entityName
     * @returns {any}
     */
    public find(key, value) {

        const entity = this.cachedData.find(key, value);

        if (!entity) {
            throw Error('Cannot find entity with ' + key + ' `' + value + '`');
        }

        return entity;
    }


    /**
     * Returns all available elements
     *
     * @returns {[{name: string, icon: string, type: string},{name: string, icon: string, type: string},{name: string, icon: string, type: string},{name: string, icon: string, type: string},{name: string, icon: string, type: string}]}
     */
    public getAvailableElements() {
        return this.availableElements;
    }

    /**
     * Returns the element that corresponds to the given type
     *
     * @param elementType
     * @returns {undefined|any}
     */
    public getElementByType(elementType: string) {
        return this.getAvailableElements().find(element => element.type === elementType);
    }

    /**
     * Register a new element to show up in entity builder or in form
     *
     * -> To hide in entity builder -> add key; hide_in_entity_builder: true
     *
     * @param element
     */
    public registerElement(element: Object) {
        this.availableElements.push([element]);
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/entities/services/entity.service.ts